import { Model } from 'infra/model'
import {
  BookedConsultation,
  Consultation as ConsultationProto,
} from '@taxfyle/api-internal/internal/consultation_pb'
import { computed, makeObservable, observable } from 'mobx'
import { Id } from 'types/definitions'
import { match } from 'utils/pattern-match-util'
import { TimeRange } from 'jobs/TimeRange'
import { durationToMinutes } from 'utils/grpc-util'
import { Room } from '../../messaging/rooms/Room'
import { RoomSessionStatus } from '../../messaging/rooms/RoomSession'
import { PrimeRoom } from 'messaging/rooms/RoomStore'

export enum ConsultationStatus {
  PendingAvailability,
  PendingBooking,
  PendingCall,
  InProgress,
  Cancelled,
  Incomplete,
  Done,
}

/**
 * The reason the consultation was not completed.
 */
export enum ConsultationIncompleteReason {
  ConsultationNotNeeded = 'ConsultationNotNeeded',
  ClientMissedCall = 'ClientMissedCall',
  ProMissedCall = 'ProMissedCall',
  PreviousProCompletedCall = 'PreviousProCompletedCall',
  ConsultationHandledViaChat = 'ConsultationHandledViaChat',
  Other = 'Other',
}

export const ConsultationCallProgressSteps = {
  UNKNOWN: { completed: 0, pending: 0 },
  PENDING: { completed: 0, pending: 33 },
  IN_PROGRESS: { completed: 33, pending: 33 },
  DONE: { completed: 67, pending: 33 },
  COMPLETED: { completed: 100, pending: 0 },
}

export type ConsultationCallProgressStepsIds =
  keyof typeof ConsultationCallProgressSteps

export default class Consultation extends Model<
  ConsultationProto.AsObject,
  Consultation
> {
  @observable id = Id.none
  @observable jobId = Id.none
  @observable status = ConsultationStatus.PendingAvailability
  @observable times: TimeRange[] = []
  @observable clientUserPublicId = Id.none
  @observable clientDisplayName = ''
  @observable providerUserPublicId = Id.none
  @observable providerDisplayName = ''
  @observable bookedTime?: TimeRange
  @observable requestedLength: number = 30
  @observable skipScheduling: boolean = false
  @observable room = Room.none
  @observable workspaceId = Id.none

  /**
   * When true, indicates that, although the consultation isn't currently booked, it was in the past.
   */
  @observable previouslyBooked = false

  constructor(private primeRoom: PrimeRoom) {
    super()
    makeObservable(this)
  }

  @computed
  get availability() {
    return this.times?.flatMap((a) =>
      a.decomposeRangeInIntervals(this.requestedLength),
    )
  }

  @computed
  get isPendingBooking() {
    return this.status === ConsultationStatus.PendingBooking
  }

  @computed
  get isPendingAvailability() {
    return this.status === ConsultationStatus.PendingAvailability
  }

  @computed
  get canClose(): boolean {
    return this.skipScheduling || this.callStep === 'DONE'
  }

  @computed
  get callStep(): ConsultationCallProgressStepsIds {
    if (this.room.roomSource.type === 'Unknown') {
      return 'PENDING'
    }

    if (this.room.roomSource.type === 'Local') {
      return 'DONE'
    }

    switch (this.status) {
      case ConsultationStatus.PendingCall:
        return 'PENDING'
      case ConsultationStatus.InProgress:
        if (this.hadRoomSession) {
          return 'DONE'
        }
        return 'IN_PROGRESS'
      case ConsultationStatus.Done:
        return 'COMPLETED'
      default:
        return 'UNKNOWN'
    }
  }

  @computed
  get hadRoomSession(): boolean {
    return this.room.sessions.some((s) => s.status === RoomSessionStatus.Ended)
  }

  parse(
    attrs: Partial<ConsultationProto.AsObject> &
      Partial<BookedConsultation.AsObject>,
  ): Partial<Consultation> {
    return {
      id: attrs.id,
      jobId: attrs.jobId,
      status:
        attrs.status !== undefined
          ? match(attrs.status, {
              [ConsultationProto.ConsultationStatus.PENDING_AVAILABILITY]: () =>
                ConsultationStatus.PendingAvailability,
              [ConsultationProto.ConsultationStatus.PENDING_BOOKING]: () =>
                ConsultationStatus.PendingBooking,
              [ConsultationProto.ConsultationStatus.PENDING_CALL]: () =>
                ConsultationStatus.PendingCall,
              [ConsultationProto.ConsultationStatus.IN_PROGRESS]: () =>
                ConsultationStatus.InProgress,
              [ConsultationProto.ConsultationStatus.DONE]: () =>
                ConsultationStatus.Done,
              [ConsultationProto.ConsultationStatus.CANCELLED]: () =>
                ConsultationStatus.Cancelled,
              [ConsultationProto.ConsultationStatus.INCOMPLETE]: () =>
                ConsultationStatus.Incomplete,
              [ConsultationProto.ConsultationStatus.UNKNOWN]: () =>
                ConsultationStatus.PendingAvailability,
            })
          : ConsultationStatus.PendingAvailability,
      times: attrs?.availability
        ? attrs.availability?.availabilityTimesList?.map((i) =>
            TimeRange.fromInterval(i),
          )
        : [],
      clientUserPublicId: attrs.clientUserPublicId,
      clientDisplayName: attrs.clientDisplayName,
      providerUserPublicId: attrs.providerUserPublicId,
      providerDisplayName: attrs.providerDisplayName,
      bookedTime: attrs.bookedTime
        ? TimeRange.fromInterval(attrs.bookedTime)
        : undefined,
      requestedLength: attrs.requestedLength
        ? durationToMinutes(attrs.requestedLength)
        : 30,
      skipScheduling: attrs.skipScheduling,
      room: this.primeRoom({ id: attrs.roomId }),
      previouslyBooked: attrs.wasBookedPreviously,
      workspaceId: attrs.workspaceId,
    }
  }

  fromJSON(json: Partial<ConsultationProto.AsObject>) {
    return this.set(json, { parse: true })
  }

  static fromJSON(
    json: Partial<ConsultationProto.AsObject>,
    primeRoom: PrimeRoom,
  ) {
    return new Consultation(primeRoom).set(json, {
      parse: true,
    })
  }
}
